"use client"

import Link from "next/link"
import { useCallback } from "react"
import { Clock } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { Article } from "@/lib/types"

interface ViralNewsSidebarProps {
  importantArticles?: Article[]
  viralArticles?: Article[]
  onLoadMoreImportant?: () => void
  hasMoreImportant?: boolean
}

function SidebarSection({
  title,
  articles,
  emptyText,
  onScrollEnd,
}: {
  title: string
  articles?: Article[]
  emptyText: string
  onScrollEnd?: () => void
}) {
  const safeArticles = articles || []
  const isImportantSection = title === "Important News"
  const articlesToRender = safeArticles
  return (
    <div className="pb-3 last:pb-0">
      <div className="px-3 pt-3 pb-0">
        <div
          className="flex items-center rounded-md"
          style={{
            backgroundColor: "#4a4a4a",
            borderRadius: "6px",
            padding: "8px 12px",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "4px",
              backgroundColor: "#D00501",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          />
          <span
            className="font-semibold"
            style={{
              fontSize: "14px",
              color: "#FFFFFF",
              marginLeft: "8px",
            }}
          >
            {title}
          </span>
        </div>
      </div>

      <CardContent className="px-3 pt-3">
        {safeArticles.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <>
            <div className="space-y-3">
              {articlesToRender.map((article) => (
                <Link key={article.id} href={`/article/${article.slug}`} className="block group">
                  <div className="flex gap-2 pb-2 border-b border-gray-200 last:border-b-0">
                    {/* Thumbnail */}
                    <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden rounded">
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <span className="text-muted-foreground text-xs">No Image</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h4
                        style={{
                          position: "relative",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                        className="group-hover:text-primary transition-colors mb-1"
                        title={article.title}
                      >
                        {article.title}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {article.read_time && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{article.read_time} min</span>
                          </div>
                        )}
                        {article.created_at && (
                          <span>{new Date(article.created_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {isImportantSection && onScrollEnd && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={onScrollEnd}
                  className="px-3 py-1 text-xs font-medium border rounded-md hover:bg-gray-100"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </div>
  )
}

export function ViralNewsSidebar({
  importantArticles,
  viralArticles,
  onLoadMoreImportant,
  hasMoreImportant = false,
}: ViralNewsSidebarProps) {
  const safeImportant = importantArticles || []
  const safeViral = viralArticles || []

  return (
    <Card 
      className="py-0" 
      style={{ 
        display: "block",
        position: "relative",
        backgroundColor: "var(--viral-news-box-bg-color, #FFFFFF)",
        background: "#fff",
        borderRadius: "5px",
        boxShadow: "1px 1px 5px rgba(0, 0, 0, .12)"
      }}
    >
      <SidebarSection
        title="Important News"
        articles={safeImportant}
        emptyText="No important news available"
        onScrollEnd={hasMoreImportant ? onLoadMoreImportant : undefined}
      />
      <SidebarSection
        title="Viral News"
        articles={safeViral.slice(0, 2)}
        emptyText="No viral news available"
      />
    </Card>
  )
}

